import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ILocationState } from '../../../models';
import { isAdmin } from '../../../extViewmodels';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import {
	accountHasComplianceEnabled,
	createContentStateWithHtmlStringValue,
	getRatingIntFromText,
} from '../../../models/UiUtils';
import { useAccountLoad, useAdminAccountQuery, useAttachmentQuery } from '../../../queries';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import {
	invalidateTestimonialTemplate,
	useGetTestimonialTemplate,
} from '../../../queries/GoogleBusiness/useGetTestimonialTemplate';
import {
	STALE_TEMPLATE,
	describeRenderFailure,
	useRenderTestimonialMutation,
} from '../../../queries/GoogleBusiness/useRenderTestimonialMutation';
import { useSetGoogleBusinessBrandingSettings } from '../../../queries/GoogleBusiness/useSetGoogleBusinessBrandingSettings';
import { useCreateSocialPostMutation } from '../../../queries/Social/useCreateSocialPostMutation';
import { ImpersonationContextViewModel } from '../../../viewmodels/AdminViewModels';
import { lastUsedComplianceRecipientEmail } from '../campaigns/CampaignDetailsSummary';
import { DEFAULT_COLORS, DEFAULT_POST_DESCRIPTIONS } from './constants';
import { useGetImageUrl } from './hooks/useGetImageUrl';
import { buildTestimonialPreview } from './interpolateTestimonialTemplate';
import { styleSheet } from './styles';
import { TestimonialControls } from './TestimonialControls';
import { TestimonialCreationDialog } from './TestimonialCreationDialog';
import { TestimonialPreview } from './TestimonialPreview';
import { resolveLayout } from './layoutBoxGeometry';
import {
	LayoutElement,
	LocationState,
	StoredPreferences,
	TestimonialCreationStep,
	TestimonialImageEditorProps,
} from './types';

const getImpersonationContextPlain = (
	ctx: ImpersonationContextViewModel | Api.IImpersonationContext | undefined
): Api.IImpersonationContext | undefined =>
	ctx && 'toJs' in ctx && typeof (ctx as ImpersonationContextViewModel).toJs === 'function'
		? (ctx as ImpersonationContextViewModel).toJs()
		: (ctx as Api.IImpersonationContext | undefined);

const isImpersonationValid = (ctx: ImpersonationContextViewModel | Api.IImpersonationContext | undefined): boolean =>
	!!(ctx && 'isValid' in ctx && (ctx as ImpersonationContextViewModel).isValid);

const STORAGE_KEY = 'testimonialEditorPreferences';

const getStoredPreferences = (): StoredPreferences | null => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? JSON.parse(stored) : null;
	} catch (error) {
		// Silently handle localStorage errors and return null for defaults
		// In production, you might want to log this, but in tests it's expected
		if (process.env.NODE_ENV !== 'test') {
			console.error('Error reading preferences from localStorage:', error);
		}
		return null;
	}
};

const savePreferences = (preferences: StoredPreferences): void => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
	} catch (error) {
		// Silently handle localStorage errors in tests
		if (process.env.NODE_ENV !== 'test') {
			console.error('Error saving preferences to localStorage:', error);
		}
	}
};

const getRandomPostDescription = (): string => {
	return DEFAULT_POST_DESCRIPTIONS[Math.floor(Math.random() * DEFAULT_POST_DESCRIPTIONS.length)];
};

export const TestimonialImageEditor: React.FC<TestimonialImageEditorProps> = ({
	testimonial,
	containerStyles,
	builderStyles,
	impersonationContext,
	accountId,
	onPostCreated,
}: TestimonialImageEditorProps) => {
	const userSession = useUserSession();
	const impersonationContextPlain = getImpersonationContextPlain(impersonationContext);
	const account = impersonationContextPlain?.account ?? userSession.account;

	// account/{id} is only readable by the account owner. When impersonating (web-app or
	// admin site), use the admin endpoint instead.
	const isImpersonating = !!impersonationContextPlain;
	const { data: accountDataUser } = useAccountLoad({
		accountId: account?.id ?? '',
		enabled: !isImpersonating,
	});
	const { data: accountDataAdmin } = useAdminAccountQuery({
		accountId: account?.id ?? '',
		enabled: isImpersonating,
	});
	const accountData = accountDataAdmin ?? accountDataUser;
	const branding = accountData?.preferences?.googleReviewPostBranding;

	const { data: template } = useGetTestimonialTemplate({ impersonationContext: impersonationContextPlain });

	const { data: imageData } = useAttachmentQuery({
		id: branding?.image?.id ?? '',
		enabled: !!branding?.image?.id,
		impersonationContext: impersonationContextPlain,
	});

	// Get stored preferences (used when not in admin/impersonation context)
	const storedPrefs = getStoredPreferences();
	const { getImageUrl } = useGetImageUrl(impersonationContext);

	// State - use account preferences as defaults when available, else localStorage
	const [bgColor, setBgColor] = useState<string>(
		branding?.backgroundColor ?? storedPrefs?.bgColor ?? DEFAULT_COLORS.background
	);
	const defaultImageAttachment = branding?.image ? (imageData ?? branding.image) : undefined;
	const [selectedBackgroundImage, setSelectedBackgroundImage] = useState<{
		slug: string;
		fileAttachment: Api.IFileAttachment;
	} | null>(defaultImageAttachment ? { slug: 'account-default', fileAttachment: defaultImageAttachment } : null);

	const [textColor, setTextColor] = useState<string>(
		branding?.foregroundColor ?? storedPrefs?.textColor ?? DEFAULT_COLORS.text
	);
	const [reviewText, setReviewText] = useState(
		(testimonial.type === 'google_review' ? testimonial.data.text : testimonial.data.feedback) || ''
	);
	const [authorName, setAuthorName] = useState(
		testimonial.type === 'google_review'
			? testimonial.data.author || ''
			: testimonial.data.contact
				? `${testimonial.data.contact.firstName || ''} ${testimonial.data.contact.lastName || ''}`.trim()
				: 'Anonymous'
	);
	const [rating, _setRating] = useState(
		testimonial.type === 'google_review' ? getRatingIntFromText(testimonial.data.rating) : testimonial.data.rating || 0
	);
	const [includeGoogleLogo, setIncludeGoogleLogo] = useState(
		testimonial.type === 'google_review'
			? (branding?.includeGoogleLogo ?? storedPrefs?.includeGoogleLogo ?? true)
			: false
	);
	const [includeAuthor, setIncludeAuthor] = useState(storedPrefs?.includeAuthor ?? true);
	const [includeStars, setIncludeStars] = useState(branding?.includeStars ?? storedPrefs?.includeStars ?? true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [sourceType] = useState<'google_review' | 'survey_response'>(testimonial.type);
	const [creationStep, setCreationStep] = useState<TestimonialCreationStep | null>(null);
	const [sendFromUser, setSendFromUser] = useState<Api.UserViewModel | null>(null);

	const [layoutOverride, setLayoutOverride] = useState<Partial<Api.IGoogleReviewPostLayout> | null>(null);
	const [selectedElement, setSelectedElement] = useState<LayoutElement>('text');

	const layout = useMemo(
		() =>
			template
				? resolveLayout(
						template,
						branding?.layout,
						{ key: branding?.templateKey, revision: branding?.templateRevision },
						layoutOverride
					)
				: null,
		[template, branding?.layout, branding?.templateKey, branding?.templateRevision, layoutOverride]
	);

	useEffect(() => {
		setLayoutOverride(null);
	}, [account?.id]);

	const [isContentTooLong, setIsContentTooLong] = useState(false);

	// Sync state when account branding first loads (accountData is async)
	const hasAppliedBrandingRef = useRef(false);
	useEffect(() => {
		if (branding && !hasAppliedBrandingRef.current) {
			hasAppliedBrandingRef.current = true;
			setBgColor(branding.backgroundColor ?? DEFAULT_COLORS.background);
			setSelectedBackgroundImage(
				branding.image ? { slug: 'account-default', fileAttachment: imageData ?? branding.image } : null
			);
			setTextColor(branding.foregroundColor ?? DEFAULT_COLORS.text);
			if (testimonial.type === 'google_review') {
				setIncludeGoogleLogo(branding.includeGoogleLogo ?? true);
			}
			setIncludeStars(branding.includeStars ?? true);
		}
	}, [branding, testimonial.type, imageData]);
	useEffect(() => {
		hasAppliedBrandingRef.current = false;
	}, [account?.id]);

	// Update selectedBackgroundImage when imageData loads (provides url for account default image)
	useEffect(() => {
		if (
			branding?.image &&
			imageData &&
			selectedBackgroundImage?.slug === 'account-default' &&
			selectedBackgroundImage.fileAttachment.id === branding.image.id &&
			!selectedBackgroundImage.fileAttachment.url
		) {
			setSelectedBackgroundImage({ slug: 'account-default', fileAttachment: imageData });
		}
	}, [branding?.image, imageData, selectedBackgroundImage]);

	// Save preferences whenever they change
	useEffect(() => {
		savePreferences({
			bgColor,
			textColor,
			includeGoogleLogo,
			includeAuthor,
			includeStars,
		});
	}, [bgColor, textColor, includeGoogleLogo, includeAuthor, includeStars]);

	// Hooks
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();

	const showsGoogleLogo = sourceType === 'google_review' && includeGoogleLogo;
	const showsMark = showsGoogleLogo || sourceType === 'survey_response';
	const showsStars = includeStars && rating > 0;

	const visibleElements: LayoutElement[] = [
		'text',
		...(showsMark ? (['logo'] as LayoutElement[]) : []),
		...(showsStars ? (['stars'] as LayoutElement[]) : []),
		...(includeAuthor ? (['author'] as LayoutElement[]) : []),
	];

	const backgroundImageUrl = selectedBackgroundImage?.fileAttachment?.url ?? null;

	const { previewHtml, previewError } = useMemo(() => {
		return buildTestimonialPreview(template, {
			backgroundColor: bgColor,
			foregroundColor: textColor,
			backgroundImage: backgroundImageUrl,
			reviewText,
			authorName,
			rating,
			includeGoogleLogo,
			includeStars,
			includeAuthor,
			source: sourceType === 'google_review' ? 'GoogleReview' : 'SurveyResponse',
			layout,
		});
	}, [
		template,
		bgColor,
		textColor,
		backgroundImageUrl,
		reviewText,
		authorName,
		rating,
		includeGoogleLogo,
		includeStars,
		includeAuthor,
		sourceType,
		layout,
	]);

	// Derived state
	const requiresSendFrom = isImpersonationValid(impersonationContext) && !sendFromUser;

	// Mutations
	const { mutateAsync: uploadImage } = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			setErrorMessage('Error uploading image. Please try again or continue without an image.');
		},
	});

	const { mutateAsync: renderTestimonial } = useRenderTestimonialMutation({
		impersonationContext: impersonationContextPlain,
	});

	const { mutate: saveBrandingDefaults, isPending: isSavingDefaults } = useSetGoogleBusinessBrandingSettings({
		impersonationContext: impersonationContextPlain,
		onError: error => errorMessages.pushApiError(error),
		onSuccess: () => toast.success('Saved as your account default.'),
	});

	const handleSaveDefaults = () => {
		if (!template || !layout) {
			return;
		}

		saveBrandingDefaults({
			backgroundColor: bgColor,
			foregroundColor: textColor,
			includeGoogleLogo,
			includeStars,
			image: selectedBackgroundImage?.fileAttachment,
			templateKey: template.key,
			templateRevision: template.revision,
			layout,
		});
	};

	const handleLayoutBoxChange = (element: LayoutElement, box: Api.IGoogleReviewPostLayoutBox) =>
		setLayoutOverride(current => ({ ...current, [element]: box }));

	const sizableElement = selectedElement === 'text' || selectedElement === 'author' ? selectedElement : null;

	const canSaveDefaults = isAdmin(userSession.user) || isImpersonationValid(impersonationContext);

	const handleFontSizeChange = (fontSize: number) => {
		if (!layout || !sizableElement) {
			return;
		}
		setLayoutOverride(current => ({ ...current, [sizableElement]: { ...layout[sizableElement], fontSize } }));
	};

	const accountIdForNav = accountId ?? impersonationContextPlain?.account?.id;

	const initialBackgroundImage = useMemo(
		() => (branding?.image ? { slug: 'account-default' as const, fileAttachment: imageData ?? branding.image } : null),
		[branding?.image, imageData]
	);

	const { mutateAsync: createSocialPost } = useCreateSocialPostMutation({
		onSuccess: async response => {
			onPostCreated?.();

			const postVm = new Api.SocialMediaPostViewModel(userSession, response);
			if (impersonationContextPlain) {
				postVm.impersonate(impersonationContextPlain);
			}
			await postVm.load();

			if (isImpersonationValid(impersonationContext) && accountIdForNav) {
				const locationState: ILocationState<
					Api.SocialMediaPostViewModel,
					{ impersonationContext: Api.IImpersonationContext }
				> = {
					viewModel: postVm,
					model: { impersonationContext: impersonationContextPlain },
				};
				fullscreenModal.history.push({
					pathname: `/accounts/${accountIdForNav}/content-calendar/post/create`,
					state: locationState,
				});
			} else {
				const locationState: LocationState = {
					viewModel: postVm,
				};
				fullscreenModal.history.push({
					pathname: `/social-media/post/edit-post-draft/${response.id}`,
					state: locationState,
				});
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			setCreationStep(null);
		},
	});

	const uploadBackgroundIfNeeded = async (): Promise<string | null> => {
		const attachment = selectedBackgroundImage?.fileAttachment;
		if (!attachment) {
			return null;
		}
		if (attachment.id) {
			return attachment.id;
		}

		const imageUrl = await getImageUrl(attachment);
		const blob = await (await fetch(imageUrl)).blob();
		const file = new File([blob], attachment.fileName || 'testimonial-background.png', { type: blob.type });
		const uploaded = await uploadImage({
			files: [file],
			resizeForSocialMedia: true,
			impersonationContext: impersonationContextPlain,
		});

		return uploaded[0]?.id ?? null;
	};

	// Handlers
	const generateImage = async (): Promise<void> => {
		if (!template || !layout) {
			toast.error('The testimonial template is still loading. Please try again in a moment.');
			return;
		}

		if (isContentTooLong) {
			toast.error('The review is too long for the text area. Shorten it or summarize it first.');
			return;
		}

		if (requiresSendFrom) {
			toast.error('Please select a user to post from before creating.');
			return;
		}

		setErrorMessage(null);
		setCreationStep('generating');

		try {
			const backgroundImageId = await uploadBackgroundIfNeeded();

			const fileAttachment = await renderTestimonial({
				templateKey: template.key,
				templateRevision: template.revision,
				source: sourceType === 'google_review' ? 'GoogleReview' : 'SurveyResponse',
				reviewText,
				authorName,
				rating,
				backgroundColor: bgColor,
				foregroundColor: textColor,
				includeGoogleLogo,
				includeStars,
				includeAuthor,
				backgroundImageId,
				layout,
			});

			await createDraft(fileAttachment?.url ?? null, fileAttachment);
		} catch (error) {
			const failure = error as Api.IOperationResultNoValue;
			const message = describeRenderFailure(failure);

			setCreationStep(null);
			toast.error(message);
			setErrorMessage(message);

			if (failure?.systemCode === STALE_TEMPLATE) {
				invalidateTestimonialTemplate({ impersonationContext: impersonationContextPlain });
			} else {
				errorMessages.pushApiError(error);
			}
		}
	};

	const createDraft = async (imageUrl: string | null, fileAttachment?: any): Promise<void> => {
		setCreationStep('creating');

		const content = createContentStateWithHtmlStringValue(getRandomPostDescription()).getRawRichTextContent();

		const isComplianceEnabled = accountHasComplianceEnabled(
			userSession,
			impersonationContext as ImpersonationContextViewModel
		);
		const sendWithComplianceEmail = localStorage.getItem(lastUsedComplianceRecipientEmail) ?? undefined;

		const createRequest: Api.ICreateSocialMediaPostRequest = {
			name: `Testimonial from ${authorName || 'Customer'}`,
			content,
			attachments: fileAttachment ? [fileAttachment] : [],
			overlayLogo: false,
			designatedTargets: sendFromUser
				? sendFromUser.toJs().socialMediaConnectedAccounts?.map((socialConnection: Api.ISocialMediaConnection) => {
						return {
							provider: socialConnection.type as Api.SocialMediaType,
							pageId: socialConnection.postTargetId,
							userId: socialConnection.userId,
						};
					})
				: [],
			googleReviewId: testimonial.type === 'google_review' ? testimonial.data.id : undefined,
			surveyResponseId: testimonial.type === 'survey_response' ? testimonial.data.id : undefined,
			sendWithCompliance: isComplianceEnabled ? true : undefined,
			sendWithComplianceEmail: isComplianceEnabled ? sendWithComplianceEmail : undefined,
		};

		try {
			await createSocialPost({
				request: createRequest,
				forUserId: sendFromUser?.id ?? userSession.user?.id,
				impersonationContext: { ...impersonationContextPlain, user: { id: sendFromUser?.id ?? userSession.user?.id } },
			});
			toast.success('Post draft created successfully!');
			setCreationStep(null);
		} catch (error) {
			toast.error('Failed to create draft post. Please try again.');
			setCreationStep(null);
			errorMessages.pushApiError(error);
		}
	};

	return (
		<div className={css(styleSheet.editorContainer, ...(containerStyles ?? []))}>
			<TestimonialControls
				builderStyles={builderStyles}
				bgColor={bgColor}
				textColor={textColor}
				reviewText={reviewText}
				authorName={authorName}
				includeGoogleLogo={includeGoogleLogo}
				includeAuthor={includeAuthor}
				includeStars={includeStars}
				sourceType={sourceType}
				isContentTooLong={isContentTooLong}
				summarizeReviewId={testimonial.type === 'google_review' ? testimonial.data.id : undefined}
				fontSize={sizableElement ? layout?.[sizableElement]?.fontSize : undefined}
				fontSizeLabel={sizableElement === 'author' ? 'Author Font Size' : 'Quote Font Size'}
				onFontSizeChange={handleFontSizeChange}
				createPostDisabled={requiresSendFrom}
				onBgColorChange={setBgColor}
				onTextColorChange={setTextColor}
				onReviewTextChange={setReviewText}
				onAuthorNameChange={setAuthorName}
				onIncludeGoogleLogoChange={() => setIncludeGoogleLogo(!includeGoogleLogo)}
				onIncludeAuthorChange={() => setIncludeAuthor(!includeAuthor)}
				onIncludeStarsChange={() => setIncludeStars(!includeStars)}
				onGenerateImage={generateImage}
				onSendFromChange={setSendFromUser}
				onBackgroundImageChange={setSelectedBackgroundImage}
				initialBackgroundImage={initialBackgroundImage}
				// Only pass when defined so mobx-react `inject(ImpersonationContextKey)` on
				// TestimonialControls can still fall back to the Provider in web-app contexts.
				// In the admin site the Provider hardcodes null, so the explicit prop wins.
				{...(impersonationContext ? { impersonationContext } : {})}
			/>
			<TestimonialPreview
				isCreatingDraft={creationStep !== null}
				errorMessage={errorMessage}
				canvas={template?.canvas}
				previewHtml={previewHtml}
				previewError={previewError}
				onCreateDraft={createDraft}
				onTextOverflowChange={setIsContentTooLong}
				layout={layout ?? undefined}
				visibleElements={visibleElements}
				selectedElement={selectedElement}
				onSelectElement={setSelectedElement}
				onLayoutBoxChange={handleLayoutBoxChange}
				reviewText={reviewText}
				textColor={textColor}
				onReviewTextChange={setReviewText}
				canSaveDefaults={canSaveDefaults}
				isSavingDefaults={isSavingDefaults}
				onSaveDefaults={handleSaveDefaults}
			/>
			<TestimonialCreationDialog step={creationStep} />
		</div>
	);
};
